import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { Dialog, Stack, Text, Strong, Actions, Button, ButtonLink, IconNewWindow, TextLinkButton, Box } from "braid-design-system";
import { useEffect, useMemo, useState } from "react";
import { useTranslations } from "@vocab/react";
import { translations } from "../../../components/ProfileCardDescription/.vocab/index.mjs";
import { text } from "./ProfileCardDescription.css.mjs";
const createOpenDialogFn = (dialogStateFn) => () => {
  dialogStateFn(true);
  const historyState = history.state;
  const newState = { ...historyState, dialogOpen: true };
  history.pushState(newState, "Dialog open");
};
const createCloseDialogFn = (dialogStateFn) => () => {
  dialogStateFn(false);
  history.go(-1);
};
const useDialogHistoryManager = (dialogStateFn) => {
  useEffect(() => {
    const windowHistoryListener = () => {
      const historyState = history.state;
      const newState = { ...historyState, dialogOpen: false };
      history.replaceState(newState, "Dialog closed");
      dialogStateFn(false);
    };
    window.addEventListener("popstate", windowHistoryListener);
    if ("scrollRestoration" in history) {
      history.scrollRestoration = "manual";
    }
    return () => {
      window.removeEventListener("popstate", windowHistoryListener);
    };
  }, [dialogStateFn]);
  const openDialog = useMemo(
    () => createOpenDialogFn(dialogStateFn),
    [dialogStateFn]
  );
  const closeDialog = useMemo(
    () => createCloseDialogFn(dialogStateFn),
    [dialogStateFn]
  );
  return {
    openDialog,
    closeDialog
  };
};
const ExternalUrlDialog = ({
  onClose,
  externalUrl,
  open,
  brand
}) => {
  const { t } = useTranslations(translations);
  return /* @__PURE__ */ jsx(
    Dialog,
    {
      id: "externalUrlDialog",
      width: "small",
      onClose,
      closeLabel: t("Close"),
      open,
      title: t("Are you sure you want to leave brand?", { brand }),
      children: /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
        /* @__PURE__ */ jsx(Text, { children: t("The link will take you to a site outside...", {
          externalUrl,
          brand,
          Strong: (v) => /* @__PURE__ */ jsx(Strong, { children: v }, "confirmLeavingMessage"),
          br: () => /* @__PURE__ */ jsx("br", {}, "confirmLeavingMessageLineBreak")
        }) }),
        /* @__PURE__ */ jsxs(Actions, { children: [
          /* @__PURE__ */ jsx(Button, { onClick: onClose, tone: "formAccent", children: t("Return") }),
          /* @__PURE__ */ jsxs(
            ButtonLink,
            {
              href: externalUrl,
              onClick: onClose,
              target: "_blank",
              rel: "noopener",
              role: "button",
              tone: "formAccent",
              variant: "ghost",
              children: [
                t("Go to site"),
                " ",
                /* @__PURE__ */ jsx(
                  IconNewWindow,
                  {
                    alignY: "lowercase",
                    title: t("Opens in a new tab"),
                    titleId: "externalLinkIcon"
                  }
                )
              ]
            }
          )
        ] })
      ] })
    }
  );
};
const internalUrls = [
  "seek.com",
  "seek.co",
  "jobsdb.com",
  "jobstreet.com"
];
const urlRegex = /((?:https?:\/\/)?(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+~#?&//=]*))/g;
const isInternalUrl = (url) => internalUrls.some((internalUrl) => url.indexOf(internalUrl) >= 0);
const sanitiseUrl = (url) => {
  const matches = url.match(/^https?:\/\//g);
  if (matches === null) {
    return `https://${url}`;
  }
  if (matches[0] === "http://") {
    return url.replace("http", "https");
  }
  return url;
};
const replaceTextWithLinks = (text2, onClickFn, linksAreKeyboardAccessible) => {
  const splitText = text2.split(urlRegex);
  const replacedText = splitText.map((subString, index) => {
    if (index % 2 !== 0) {
      const url = sanitiseUrl(subString);
      return /* @__PURE__ */ jsx(
        TextLinkButton,
        {
          onClick: () => onClickFn(url),
          weight: "weak",
          tabIndex: linksAreKeyboardAccessible ? 0 : -1,
          children: subString
        },
        index
      );
    }
    return subString;
  });
  return replacedText;
};
const ProfileCardDescription = ({
  text: text$1,
  data,
  linksAreKeyboardAccessible = true,
  brand = "SEEK",
  replaceUrlTextWithLinks = true
}) => {
  const [displayDialog, setDisplayDialog] = useState(false);
  const { openDialog, closeDialog } = useDialogHistoryManager(setDisplayDialog);
  const [currentExternalUrl, setCurrentExternalUrl] = useState("");
  const onClickFn = (url) => {
    if (isInternalUrl(url)) {
      window.open(url, "_blank", "noopener");
    } else {
      setCurrentExternalUrl(url);
      openDialog();
    }
  };
  const onClose = () => {
    closeDialog();
  };
  const replacedText = replaceUrlTextWithLinks ? replaceTextWithLinks(text$1, onClickFn, linksAreKeyboardAccessible) : text$1;
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      Box,
      {
        className: text,
        component: "span",
        data: data ? { ...data } : { "hj-masked": "true" },
        children: replacedText
      }
    ),
    /* @__PURE__ */ jsx(
      ExternalUrlDialog,
      {
        externalUrl: currentExternalUrl,
        open: displayDialog,
        onClose,
        brand
      }
    )
  ] });
};
ProfileCardDescription.displayName = "ProfileCardDescription";
export {
  ProfileCardDescription,
  useDialogHistoryManager
};
